import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Border_Sides = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🟦 CSS Border Sides</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS lets you control the border of each side of an element individually.
        Instead of applying a border to all sides, you can use{" "}
        <code>border-top</code>, <code>border-right</code>,{" "}
        <code>border-bottom</code>, and <code>border-left</code>.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`selector {
  border-top: width style color;
  border-right: width style color;
  border-bottom: width style color;
  border-left: width style color;
}`}
        />
      </div>

      {/* Individual Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">Examples: Individual Sides</h2>
        <CodeCopyBlock
          code={`p { border-top: 4px solid red; }
p { border-right: 4px dashed blue; }
p { border-bottom: 4px dotted green; }
p { border-left: 4px double orange; }`}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            marginTop: "1rem",
          }}
        >
          <div style={{ borderTop: "4px solid red", padding: "10px" }}>
            Top border
          </div>
          <div style={{ borderRight: "4px dashed blue", padding: "10px" }}>
            Right border
          </div>
          <div style={{ borderBottom: "4px dotted green", padding: "10px" }}>
            Bottom border
          </div>
          <div style={{ borderLeft: "4px double orange", padding: "10px" }}>
            Left border
          </div>
        </div>
      </div>

      {/* Combining Sides */}
      <div className="tutorial-section">
        <h2 className="section-title">Combine Multiple Sides</h2>
        <p>
          You can also mix different border sides together for creative effects.
        </p>
        <CodeCopyBlock
          code={`div {
  border-top: 5px solid red;
  border-bottom: 5px solid green;
  border-left: 5px solid blue;
  border-right: 5px solid orange;
}`}
        />
        <div
          style={{
            borderTop: "5px solid red",
            borderBottom: "5px solid green",
            borderLeft: "5px solid blue",
            borderRight: "5px solid orange",
            padding: "15px",
            marginTop: "0.5rem",
          }}
        >
          🔴🟢🔵🟠 Different colors on each side
        </div>
      </div>

      {/* Shorthand Borders */}
      <div className="tutorial-section">
        <h2 className="section-title">Border Side Shorthand</h2>
        <p>Each side also supports its own shorthand. For example:</p>
        <CodeCopyBlock
          code={`div { border-top: 3px solid purple; }
div { border-right: 6px dotted teal; }
div { border-bottom: 8px dashed pink; }
div { border-left: 10px double brown; }`}
        />
      </div>

      {/* Transparent Borders */}
      <div className="tutorial-section">
        <h2 className="section-title">Transparent Borders</h2>
        <p>
          You can hide borders for specific sides using <code>transparent</code>
          .
        </p>
        <CodeCopyBlock
          code={`div {
  border: 5px solid black;
  border-top: 5px solid transparent; /* hides the top border */
}`}
        />
        <div
          style={{
            border: "5px solid black",
            borderTop: "5px solid transparent",
            padding: "15px",
            marginTop: "0.5rem",
          }}
        >
          ⬆️ Top border is hidden (transparent)
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          After mastering border sides, explore{" "}
          <a href="/css/border-radius" className="learn-more">
            CSS Border Radius
          </a>{" "}
          to create rounded borders and shapes.
        </p>
      </div>
    </div>
  );
};

export default Border_Sides;
